import type { BasicEnrichmentRequest, EnrichmentRequest } from 'cadenza/workbook/api/enrichment-api';
import type { EnrichmentType } from 'cadenza/workbook/api/enrichment-type';
import type { EnrichmentDialogOptions } from 'cadenza/workbook/enrichment/enrichment-dialog/enrichment-dialog';
import type { AllPropertiesAssigned } from 'cadenza/utils/type-util';

export async function showEnrichmentDialog<T extends EnrichmentType>(type: T, options: EnrichmentDialogOptions<T>) {
  const { EnrichmentDialog } = await import(
    /* webpackChunkName: "enrichment-dialog" */ 'cadenza/workbook/enrichment/enrichment-dialog/enrichment-dialog'
  );
  return new EnrichmentDialog(type, options).connectAndShow();
}

/**
 * these types are still fully optional when executing a rerun
 */
type IgnoredExplicitRerunProperties = Extract<
  keyof BasicEnrichmentRequest,
  // not part of the rerun configuration
  | 'dataScopeSettings'
  | 'dataProtectionContextId'
  | 'dataProtectionDescription'
  // due to historic reasons, we have to keep this optional
  | 'associatedBasicObjecttypeId'
>;

type RequiredRerunProperties = 'enrichedObjecttypeId' | 'existingEnrichmentObjecttypeId';

/**
 * This reminds people to update the code for rerunning enrichments by requiring them to declare
 * all properties except the ones declared IgnoredRerunProperties
 */
type RerunEnrichmentOptions<T extends EnrichmentType> = AllPropertiesAssigned<
  Omit<EnrichmentRequest<T>, IgnoredExplicitRerunProperties>
> & // make all IgnoredExplicitRerunProperties explicitly required
  Partial<Pick<EnrichmentRequest<T>, IgnoredExplicitRerunProperties>> & // re-add the ignored properties as optional parameters again
  Required<Pick<EnrichmentRequest<T>, RequiredRerunProperties>>; // make required properties required

export async function showRerunEnrichmentDialog<T extends EnrichmentType>(type: T, options: RerunEnrichmentOptions<T>) {
  return showEnrichmentDialog(type, options as unknown as EnrichmentDialogOptions<T>);
}
